import React, { useState } from 'react';
import { Send, Phone, Mail, MapPin, Clock } from 'lucide-react';
import { businessInfo } from '../data/mock';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mock form submission
    console.log('Form submitted:', formData);
    
    // Show success message
    toast.success('Quote request sent! We\'ll contact you within 30 minutes.', {
      duration: 5000
    });
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      service: '',
      message: ''
    });
  };

  return (
    <section id="contact" className="py-24 bg-slate-950">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Get In Touch
          </h2>
          <p className="text-xl text-gray-400">
            Need a quote or have questions? Fill out the form below or call us 
            directly for immediate assistance
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 max-w-7xl mx-auto">
          {/* Contact Information */}
          <div className="lg:col-span-1 space-y-8">
            {/* Emergency Call Card */}
            <div className="bg-blue-600 rounded-xl p-8 shadow-2xl">
              <h3 className="text-2xl font-bold text-white mb-4">Emergency?</h3>
              <p className="text-blue-100 mb-6">
                Call us now for immediate roadside assistance
              </p>
              <a 
                href={`tel:${businessInfo.phone}`}
                className="flex items-center justify-center space-x-3 bg-white text-blue-600 px-6 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors duration-200"
              >
                <Phone className="w-6 h-6" />
                <span>{businessInfo.phone}</span>
              </a>
            </div>

            {/* Contact Details */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-8 space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Phone</p>
                  <a href={`tel:${businessInfo.phone}`} className="text-white font-semibold hover:text-blue-400 transition-colors">
                    {businessInfo.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Email</p>
                  <a href={`mailto:${businessInfo.email}`} className="text-white font-semibold hover:text-blue-400 transition-colors break-all">
                    {businessInfo.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Coverage</p>
                  <p className="text-white font-semibold">Nationwide UK</p>
                  <p className="text-gray-400 text-sm">Based in Bolton, Manchester</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Hours</p>
                  <p className="text-white font-semibold">24/7 Service</p>
                  <p className="text-gray-400 text-sm">Available Every Day</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-8 md:p-12">
              <h3 className="text-2xl font-bold text-white mb-6">Request a Quote</h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-400 mb-2 font-medium">Your Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors duration-200"
                      placeholder="John Smith"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-400 mb-2 font-medium">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors duration-200"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-400 mb-2 font-medium">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors duration-200"
                      placeholder="07XXX XXXXXX"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-400 mb-2 font-medium">Service Required *</label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors duration-200"
                    >
                      <option value="">Select a service</option>
                      <option value="breakdown">24/7 Breakdown Recovery</option>
                      <option value="towing">Vehicle Towing</option>
                      <option value="roadside">Roadside Assistance</option>
                      <option value="accident">Accident Recovery</option>
                      <option value="jumpstart">Jump Start</option>
                      <option value="battery">Flat Battery Service</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-400 mb-2 font-medium">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="5"
                    className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors duration-200 resize-none"
                    placeholder="Tell us about your situation or any specific requirements..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-blue-500/50"
                >
                  <Send className="w-5 h-5" />
                  <span>Send Quote Request</span>
                </button>

                <p className="text-gray-500 text-sm text-center">
                  We'll get back to you within 30 minutes during business hours
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
